import { supabaseClient } from './supabase';
import { COMPACT_DEVELOPMENT_MIN_DENSITY } from './compactDevelopmentService';

export type GeographyType = 'City' | 'County';

export type TransportationPeerMetricKey =
  | 'transportation_burden_percentile'
  | 'commute_time_percentile'
  | 'traffic_percentile'
  | 'ev_charging_percentile';

export interface TransportationPeerMetric {
  key: TransportationPeerMetricKey;
  label: string;
  statewidePercentile: number | null;
  peerPercentile: number | null;
  peerMedianPercentile: number | null;
  gapFromPeerMedian: number | null;
}

export interface TransportationPeerComparisonResult {
  locationName: string;
  geographyType: GeographyType;
  year: number;
  selectedGeofips?: string;
  densityValuePerSqMi: number | null;
  densityPeerGroupLabel: string;
  peerGroupN: number;
  metrics: TransportationPeerMetric[];
  topDrivers: { label: string; reason: string }[];
}

type Row = {
  location_name: string | null;
  geofips: string | null;
  transportation_burden_percentile?: number | null;
  commute_time_percentile?: number | null;
  traffic_percentile?: number | null;
  ev_charging_percentile?: number | null;
};

const METRIC_META: { key: TransportationPeerMetricKey; label: string }[] = [
  { key: 'transportation_burden_percentile', label: 'Transportation Burden' },
  { key: 'commute_time_percentile', label: 'Commute Time' },
  { key: 'traffic_percentile', label: 'Traffic' },
  { key: 'ev_charging_percentile', label: 'EV Charging (benefit)' }
];

function toNum(v: any): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function computePeerPercentile(values: number[], selected: number): number {
  if (!values.length) return 0;
  const n = values.length;
  const le = values.reduce((acc, v) => acc + (v <= selected ? 1 : 0), 0);
  return (le / n) * 100;
}

function computeMedian(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) return (sorted[mid - 1] + sorted[mid]) / 2;
  return sorted[mid];
}

function buildGeofipsVariants(geofipsRaw: string): string[] {
  const s = String(geofipsRaw).trim();
  if (!s) return [];
  const out = new Set<string>();
  out.add(s);

  if (/^\d+$/.test(s)) {
    if (s.length === 6) out.add(`0${s}`);
    if (s.length === 4) out.add(`0${s}`);
    if (s.length === 1) out.add(`0${s}`);

    const unpadded = s.replace(/^0+/, '') || '0';
    out.add(unpadded);
  }

  return Array.from(out);
}

function densityLabel(d: number | null) {
  if (d == null) return 'Unknown density';
  return d >= COMPACT_DEVELOPMENT_MIN_DENSITY ? 'High-density peers' : 'Low-density peers';
}

export async function getTransportationPeerComparison(params: {
  locationName: string;
  geographyType: GeographyType;
  geofips?: string | number | null;
  year?: number;
}): Promise<TransportationPeerComparisonResult | null> {
  const { locationName, geographyType, geofips, year = 2023 } = params;
  const geofipsStr = geofips != null ? String(geofips).trim() : '';
  const variants = geofipsStr ? buildGeofipsVariants(geofipsStr) : [];

  // 1) Fetch all candidate metric percentiles for this year + geography type
  const pageSize = 1000;
  let from = 0;
  const all: Row[] = [];
  while (true) {
    const query = supabaseClient
      .from('livability_index_precalculated')
      .select(
        [
          'location_name',
          'geofips',
          'transportation_burden_percentile',
          'commute_time_percentile',
          'traffic_percentile',
          'ev_charging_percentile'
        ].join(',')
      )
      .eq('geography_type', geographyType)
      .eq('year', year)
      .order('geofips', { ascending: true })
      .range(from, from + pageSize - 1);

    const { data, error } = await query;
    if (error) throw error;
    const rows = (Array.isArray(data) ? data : []) as unknown as Row[];
    if (!rows.length) break;
    all.push(...rows);
    if (rows.length < pageSize) break;
    from += pageSize;
  }

  const allWithAnyMetric = all.filter((r) => {
    const has =
      r.transportation_burden_percentile != null ||
      r.commute_time_percentile != null ||
      r.traffic_percentile != null ||
      r.ev_charging_percentile != null;
    return Boolean(has);
  });
  if (!allWithAnyMetric.length) return null;

  // 2) Resolve selected row (by geofips first, then by location_name fallback)
  const selectedRow =
    (variants.length
      ? allWithAnyMetric.find((r) => r.geofips && variants.includes(String(r.geofips).trim()))
      : null) ||
    allWithAnyMetric.find((r) => (r.location_name || '').trim().toLowerCase() === locationName.trim().toLowerCase()) ||
    null;

  if (!selectedRow) return null;

  // 3) Load population density for all candidates (used to form density peer group)
  const uniqueGeofips = Array.from(
    new Set(allWithAnyMetric.map((r) => (r.geofips || '').trim()).filter(Boolean))
  );

  const densityMap = new Map<string, number | null>();
  const densityChunkSize = 300;
  for (let i = 0; i < uniqueGeofips.length; i += densityChunkSize) {
    const chunk = uniqueGeofips.slice(i, i + densityChunkSize);
    const geofipsNumbers = chunk
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0);

    if (!geofipsNumbers.length) continue;

    const { data: densRows, error: densErr } = await supabaseClient
      .from('population_density')
      .select('geofips,population_density')
      .in('geofips', geofipsNumbers);
    if (densErr) throw densErr;

    (Array.isArray(densRows) ? densRows : []).forEach((r: any) => {
      const key = String(r.geofips).trim();
      densityMap.set(key, toNum(r.population_density));
    });
  }

  const selectedDensity = densityMap.get(String(selectedRow.geofips || '').trim()) ?? null;
  const selectedHighDensity = selectedDensity != null && selectedDensity >= COMPACT_DEVELOPMENT_MIN_DENSITY;

  // Density peer group definition:
  // - treat missing density as low-density (so we still get a group)
  const densityPeerGroup = allWithAnyMetric.filter((r) => {
    const g = String(r.geofips || '').trim();
    const d = densityMap.get(g) ?? null;
    const isHigh = d != null ? d >= COMPACT_DEVELOPMENT_MIN_DENSITY : false;
    return isHigh === selectedHighDensity;
  });

  const peerGroupN = densityPeerGroup.length;
  const densityPeerGroupLabel = densityLabel(selectedDensity);

  const getMetricValues = (key: TransportationPeerMetricKey): number[] => {
    return densityPeerGroup
      .map((r) => toNum((r as any)[key]))
      .filter((v): v is number => v != null);
  };

  const selectedMetric = (key: TransportationPeerMetricKey): number | null => toNum((selectedRow as any)[key]);

  const metrics: TransportationPeerMetric[] = METRIC_META.map((m) => {
    const statewidePercentile = selectedMetric(m.key);
    const peerVals = getMetricValues(m.key);
    const peerPercentile =
      statewidePercentile == null || !peerVals.length ? null : computePeerPercentile(peerVals, statewidePercentile);
    const peerMedianPercentile = peerVals.length ? computeMedian(peerVals) : null;
    const gapFromPeerMedian =
      statewidePercentile == null || peerMedianPercentile == null ? null : statewidePercentile - peerMedianPercentile;

    return {
      key: m.key,
      label: m.label,
      statewidePercentile,
      peerPercentile,
      peerMedianPercentile,
      gapFromPeerMedian
    };
  });

  // 4) Pick top drivers (highest "worse-ness")
  // - burdens/traffic: higher percentile = worse
  // - ev_charging_percentile: higher is better, so worse-ness = 100 - ev
  const driverScores = metrics
    .map((m) => {
      if (m.peerPercentile == null) return null;
      if (m.key === 'ev_charging_percentile') {
        return { label: m.label, reason: 'Lower EV charging benefit among peers', score: 100 - m.peerPercentile };
      }
      return { label: m.label, reason: 'Higher burden/impact among peers', score: m.peerPercentile };
    })
    .filter(Boolean) as { label: string; reason: string; score: number }[];

  driverScores.sort((a, b) => b.score - a.score);
  const topDrivers = driverScores.slice(0, 2).map((d) => ({ label: d.label, reason: d.reason }));

  return {
    locationName,
    geographyType,
    year,
    selectedGeofips: geofipsStr || undefined,
    densityValuePerSqMi: selectedDensity,
    densityPeerGroupLabel,
    peerGroupN,
    metrics,
    topDrivers
  };
}

